import { useEffect, useMemo, useState } from "react";
import {
  Select,
  MenuItem,
  List,
  SelectChangeEvent,
  ListItemButton,
  ListItemText,
  createTheme,
  ThemeProvider,
  ListItemIcon,
  Tooltip,
  styled,
  TooltipProps,
  tooltipClasses,
  IconButton,
} from "@mui/material";
import { Edit, OpenInNew, PictureAsPdf } from "@mui/icons-material";
import { useMainStore } from "../../Zustand/main.store";
import SidebarForm from "./FormSidebar/SidebarForm";
import { useSidebarFormStore } from "../../Zustand/sidebarForm.store";
import ManageMunicipalityModal from "./FormSidebar/ManageMunicipalityModal";
import {
  useApiGetStates,
  useApiGetMunicipalitiesByState,
  useApiGetDocumentListByMunicipality,
} from "../../API/documentationCatalogs.api";
import {
  EC2Municipality,
  States,
  EC2Document,
} from "../../API/types/documentationCatalogs.types";
import { useNotificationStore } from "../../Zustand/notification.store";

const theme = createTheme({
  palette: {
    primary: {
      main: "#193F70",
      light: "#2654C0",
    },
  },
});

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#333539",
    fontSize: theme.typography.pxToRem(16),
  },
}));

const Sidebar = () => {
  /**
   * Custom hooks
   */
  // Main Store
  const setPdfSelectedId = useMainStore((s) => s.setPdfSelectedId);
  const showCustomStateInput = useSidebarFormStore(
    (s) => s.showCustomStateInput
  );
  const selectedPdfId = useMainStore((s) => s.selectedPdfId);
  // Sidebar Store
  const setShowCustomStateInput = useSidebarFormStore(
    (s) => s.setShowCustomStateInput
  );
  const showCustomMunicipalityInput = useSidebarFormStore(
    (s) => s.showCustomMunicipalityInput
  );
  const setShowCustomMunicipalityInput = useSidebarFormStore(
    (s) => s.setShowCustomMunicipalityInput
  );
  const setCustomState = useSidebarFormStore((s) => s.setCustomState);
  const setCustomMunicipality = useSidebarFormStore(
    (s) => s.setCustomMunicipality
  );
  const setWebsiteUrl = useSidebarFormStore((s) => s.setWebsiteUrl);
  const showNotification = useNotificationStore((s) => s.show);

  /**
   * States
   */
  const [currentState, setCurrentState] = useState<States | null>(null);
  const [currentMunicipality, setCurrentMunicipality] =
    useState<EC2Municipality | null>(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [selectedMunicipalityForManage, setSelectedMunicipalityForManage] =
    useState<EC2Municipality | null>(null);
  const [states, setStates] = useState<States[]>([]);
  const [municipalities, setMunicipalities] = useState<EC2Municipality[]>([]);
  const [documents, setDocuments] = useState<EC2Document[]>([]);
  const [municipalityUrls, setMunicipalityUrls] = useState<
    string[] | undefined
  >([""]);

  /**
   * API
   */
  // 🟣 API - Get states
  const { call: callGetStates } = useApiGetStates({
    onCompleted: (res) => {
      setStates(res.data);
    },
    onError: (err) => {
      showNotification(err?.detail, "error");
      setStates([]);
    },
  });

  // 🟣 API - Get municipalities by state
  const { call: callGetMunicipalitiesByState } = useApiGetMunicipalitiesByState(
    {
      onCompleted: (res) => {
        setMunicipalities(res.data);
      },
      onError: (err) => {
        err?.status !== 404 && showNotification(err?.detail, "error");
      },
    }
  );

  // 🟣 API - Get documents by municipality
  const { call: callGetDocumentsByMunicipality } =
    useApiGetDocumentListByMunicipality({
      onCompleted: (res) => {
        setDocuments(res.data.documents ? res.data.documents : []);
        setMunicipalityUrls(res.data.municipality_link);
      },
      onError: (err) => {
        err?.status !== 404 && showNotification(err?.detail, "error");
        setDocuments([]);
        setMunicipalityUrls(undefined);
      },
    });

  /**
   * Callbacks
   */
  // 🟤 Cb -Handle state change
  const handleStateChange = (event: SelectChangeEvent<string>) => {
    const stateIdStr = event.target.value;
    setCurrentMunicipality(null);

    setPdfSelectedId(null);
    setShowCustomStateInput(false);
    setShowCustomMunicipalityInput(false);
    if (stateIdStr === "missingState") {
      setShowCustomStateInput(true);
      setWebsiteUrl("");
      setCustomState("");
      setCustomMunicipality("");
      return;
    }
    // Call municipalities API when a state is selected
    if (stateIdStr && stateIdStr !== "") {
      const stateId = parseInt(stateIdStr);
      const selectedState = states.find((s) => s.stateId === stateId) || null;
      setCurrentState(selectedState);
      if (selectedState) {
        callGetMunicipalitiesByState(selectedState.stateId).catch(() => {
          setMunicipalities([]);
        });
      }
    } else {
      setCurrentState(null);
    }
  };

  // Handle municipality change dropdown
  const handleMunicipalityChange = (event: SelectChangeEvent<string>) => {
    const municipalityIdStr = event.target.value;
    if (municipalityIdStr === "missingMunicipality") {
      setShowCustomMunicipalityInput(true);
      setCustomMunicipality("");
      setWebsiteUrl("");
      setCurrentMunicipality(null);
    } else {
      setPdfSelectedId(null);
      setShowCustomMunicipalityInput(false);
      if (municipalityIdStr && municipalityIdStr !== "") {
        const municipalityId = parseInt(municipalityIdStr);
        const selected =
          municipalities.find((m) => m.municipalityId === municipalityId) ||
          null;
        setCurrentMunicipality(selected);
      } else {
        setCurrentMunicipality(null);
      }
    }
  };

  const handlePdfClick = (documentId: number, path: string) => {
    setPdfSelectedId(documentId);
  };

  const openLinkInNewTab = (url: string) => {
    window.open(url, "_blank");
  };

  // Trim municipality
  const getMunicipalityLabel = (name: string) => name.trim();

  /**
   * Memos
   */
  // 🔵 Memo - Sorted municipalities
  const sortedMunicipalities = useMemo(
    () =>
      [...municipalities].sort((a, b) =>
        getMunicipalityLabel(a.municipalityName).localeCompare(
          getMunicipalityLabel(b.municipalityName)
        )
      ),
    [municipalities]
  );

  /**
   * Effects
   */
  // 🟡 Effect - Get state list when open
  useEffect(() => {
    callGetStates().catch(() => {
      setStates([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🟡 Effect - Get documents when municipality changes
  useEffect(() => {
    if (currentMunicipality?.municipalityId) {
      callGetDocumentsByMunicipality(currentMunicipality.municipalityId).catch(
        () => setDocuments([])
      );
    } else {
      setDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMunicipality?.municipalityId]);

  // 🟡 Effect - Reset selected pdf id wheneve state or municipality change
  useEffect(() => {
    setPdfSelectedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMunicipality, currentState]);

  return (
    <div className="sticky top-[calc(64px+10px)] h-full overflow-y-auto w-72 pt-2">
      <div className="p-4">
        <Select
          value={currentState?.stateId?.toString() ?? ""}
          displayEmpty
          onChange={handleStateChange}
          fullWidth
          sx={{ mb: 2 }}
        >
          <MenuItem value="" disabled>
            Select a State
          </MenuItem>
          <MenuItem value="missingState">Missing State?</MenuItem>
          {states
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((state) => (
              <MenuItem key={state.stateId} value={state.stateId.toString()}>
                {state.name}
              </MenuItem>
            ))}
        </Select>

        {!showCustomStateInput && (
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 16 }}
          >
            <Select
              value={
                showCustomMunicipalityInput
                  ? "missingMunicipality"
                  : currentMunicipality?.municipalityId?.toString() ?? ""
              }
              displayEmpty
              onChange={handleMunicipalityChange}
              fullWidth
              disabled={!currentState || showCustomStateInput}
              sx={{ mb: 0 }}
              renderValue={(selectedValue) => {
                if (!selectedValue) {
                  return <p>Select a Municipality</p>;
                }
                if (selectedValue === "missingMunicipality") {
                  return <p>Missing Municipality?</p>;
                }
                const found = municipalities.find(
                  (m) => m.municipalityId.toString() === selectedValue
                );
                return found
                  ? getMunicipalityLabel(found.municipalityName)
                  : selectedValue;
              }}
            >
              <MenuItem value="" disabled>
                Select a Municipality
              </MenuItem>
              <MenuItem value="missingMunicipality">
                Missing Municipality?
              </MenuItem>
              {sortedMunicipalities.map((municipality) => (
                <MenuItem
                  key={municipality.municipalityId}
                  value={municipality.municipalityId.toString()}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="flex-1">
                    {getMunicipalityLabel(municipality.municipalityName)}
                  </span>
                  <IconButton
                    aria-label="manage-municipality"
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedMunicipalityForManage(municipality);
                      setManageModalOpen(true);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

        {(showCustomStateInput || showCustomMunicipalityInput) && (
          <SidebarForm
            isNewState={showCustomStateInput}
            currentState={currentState}
            setCurrentMunicipality={setCurrentMunicipality}
          />
        )}

        {currentMunicipality && (
          <ThemeProvider theme={theme}>
            {/* PDF List */}
            <List>
              {municipalityUrls &&
                municipalityUrls.length > 0 &&
                municipalityUrls.map((url, index) => (
                  <CustomTooltip
                    key={index}
                    title={url.length >= 18 ? url : ""}
                    placement="top-end"
                  >
                    <ListItemButton
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        "&:hover": {
                          bgcolor: "primary.light",
                        },
                      }}
                      onClick={() => openLinkInNewTab(url)}
                    >
                      <ListItemIcon sx={{ color: "white", minWidth: 27 }}>
                        <OpenInNew sx={{ fontSize: "20px" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${
                          currentMunicipality?.municipalityName
                        } Scrape Link ${index + 1}`}
                        primaryTypographyProps={{
                          style: {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          },
                        }}
                      />
                    </ListItemButton>
                  </CustomTooltip>
                ))}
              {documents
                .filter((item) => item.type === "pdf")
                .map((item) => (
                  <CustomTooltip
                    key={item.documentId}
                    title={item.name.length >= 18 ? item.name : ""}
                    placement="top-end"
                  >
                    <ListItemButton
                      sx={{
                        bgcolor:
                          selectedPdfId === item.documentId
                            ? "primary.light"
                            : "primary.main",
                        color: "white",
                        "&:hover": {
                          bgcolor: "primary.light",
                        },
                      }}
                      onClick={() => handlePdfClick(item.documentId, item.path)}
                    >
                      <ListItemIcon sx={{ color: "white", minWidth: 27 }}>
                        <PictureAsPdf sx={{ fontSize: "20px" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          style: {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          },
                        }}
                      />
                    </ListItemButton>
                  </CustomTooltip>
                ))}
            </List>
          </ThemeProvider>
        )}
      </div>
      {/* Modal for managing municipalities */}
      <ManageMunicipalityModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        municipality={selectedMunicipalityForManage}
      />
    </div>
  );
};

export default Sidebar;
